import { useMemo, useState, useEffect } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import type { AssetType, MarketplaceAsset } from '../../types/marketplace';
import AssetCard from './AssetCard';
import AssetPreviewDrawer from './AssetPreviewDrawer';

const ALL_TYPES: AssetType[] = ['persona', 'skill', 'tool', 'mcp', 'knowledge', 'workflow'];

const TYPE_LABELS: Record<AssetType, string> = {
  persona: '人设',
  skill: '技能',
  tool: '工具',
  mcp: 'MCP 工具',
  knowledge: '知识',
  workflow: '工作流',
};

const SOURCE_LABELS: Record<string, string> = {
  builtin: '内置',
  plugins: '精选插件',
  derived: '衍生',
  custom: '自定义',
  marketplace: '市场',
  manual: '手动',
};

interface AssetMarketplaceProps {
  assets: MarketplaceAsset[];
  initialType?: AssetType | 'all';
  title?: string;
  hideTypeFilter?: boolean;
  onToggle?: (asset: MarketplaceAsset) => void;
  onApply?: (asset: MarketplaceAsset) => void;
  onEdit?: (asset: MarketplaceAsset) => void;
  /** B17：透传给技能卡片的「设为全局」操作（权限由调用方控制） */
  onSetGlobal?: (asset: MarketplaceAsset) => void;
  applyLabel?: string;
  removeLabel?: string;
  /**
   * 「说人话搜索」路由推荐：返回按相关度排序的资产 id 列表；
   * 返回 null 或抛错时回退本地包含过滤（诚实降级）。
   */
  onSearchRoute?: (term: string) => Promise<string[] | null>;
}

export default function AssetMarketplace({
  assets,
  initialType = 'all',
  title = '资产市场',
  hideTypeFilter = false,
  onToggle,
  onApply,
  onEdit,
  onSetGlobal,
  applyLabel,
  removeLabel,
  onSearchRoute,
}: AssetMarketplaceProps) {
  const [selectedType, setSelectedType] = useState<AssetType | 'all'>(initialType);
  // 侧边栏二级入口（/marketplace?tab=xxx）只改 query 不卸载组件，
  // initialType 变化时必须同步进内部筛选态，否则点击无反应；
  // 经本地包装触发，与下方 routeRanking 重置同理（react-hooks/set-state-in-effect）
  useEffect(() => {
    const tick = () => setSelectedType(initialType);
    tick();
  }, [initialType]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [tag, setTag] = useState('');
  const [source, setSource] = useState('');
  const [presetPack, setPresetPack] = useState('');
  const [previewAsset, setPreviewAsset] = useState<MarketplaceAsset | null>(null);
  // 路由推荐排序结果（记录对应搜索词，防止旧词结果串到新词上）
  const [routeRanking, setRouteRanking] = useState<{ term: string; ids: string[] } | null>(null);

  useEffect(() => {
    if (!onSearchRoute) return;
    const term = search.trim();
    if (!term) {
      // 通过本地包装触发重置，避免在 effect 体内直接同步调用 setState（react-hooks/set-state-in-effect）
      const tick = () => {
        setRouteRanking(null);
      };
      tick();
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      onSearchRoute(term)
        .then((ids) => {
          if (!cancelled) setRouteRanking(ids ? { term, ids } : null);
        })
        .catch(() => {
          // 路由接口失败 → 回退本地包含过滤（诚实降级）
          if (!cancelled) setRouteRanking(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [search, onSearchRoute]);

  const categories = useMemo(
    () => [...new Set(assets.map((a) => a.category).filter(Boolean))] as string[],
    [assets]
  );
  const tags = useMemo(
    () => [...new Set(assets.flatMap((a) => a.tags || []).filter(Boolean))] as string[],
    [assets]
  );
  const sources = useMemo(
    () => [...new Set(assets.map((a) => a.source).filter(Boolean))] as string[],
    [assets]
  );
  const presetPacks = useMemo(
    () => [...new Set(assets.map((a) => a.presetPack).filter(Boolean))] as string[],
    [assets]
  );

  const filteredAssets = useMemo(() => {
    const term = search.trim();
    const base = assets.filter((a) => {
      if (selectedType !== 'all' && a.type !== selectedType) return false;
      if (category && a.category !== category) return false;
      if (tag && !(a.tags || []).includes(tag)) return false;
      if (source && a.source !== source) return false;
      if (presetPack && a.presetPack !== presetPack) return false;
      return true;
    });
    if (!term) return base;
    // 路由推荐生效：按相关度排序展示（未入选的资产视为不相关）
    if (routeRanking && routeRanking.term === term) {
      const order = new Map(routeRanking.ids.map((id, index) => [id, index]));
      return base
        .filter((a) => order.has(a.id))
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }
    // 本地包含过滤兜底
    const lowered = term.toLowerCase();
    return base.filter(
      (a) =>
        a.displayName.toLowerCase().includes(lowered) ||
        (a.description || '').toLowerCase().includes(lowered) ||
        (a.category || '').toLowerCase().includes(lowered) ||
        (a.tags || []).some((t) => t.toLowerCase().includes(lowered))
    );
  }, [assets, selectedType, search, category, tag, source, presetPack, routeRanking]);

  const routeRankingActive = Boolean(search.trim() && routeRanking && routeRanking.term === search.trim());

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setTag('');
    setSource('');
    setPresetPack('');
  };

  const hasFilters = search || category || tag || source || presetPack;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="flex items-center justify-between">
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div className="text-muted" style={{ fontSize: 13 }}>
          共 {filteredAssets.length} 个资产
          {routeRankingActive && <span style={{ marginLeft: 8 }}>· 已按相关度智能排序</span>}
        </div>
      </div>

      {!hideTypeFilter && (
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button
            className={`tab ${selectedType === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedType('all')}
          >
            全部
          </button>
          {ALL_TYPES.map((t) => (
            <button
              key={t}
              className={`tab ${selectedType === t ? 'active' : ''}`}
              onClick={() => setSelectedType(t)}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 16 }}>
        <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 200, position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted)' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索名称、描述、分类或标签..."
              style={{ paddingLeft: 34 }}
            />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ flex: 1, minWidth: 140 }}>
            <option value="">全部分类</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select value={tag} onChange={(e) => setTag(e.target.value)} style={{ flex: 1, minWidth: 140 }}>
            <option value="">全部标签</option>
            {tags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {sources.length > 0 && (
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              style={{ flex: 1, minWidth: 140 }}
              aria-label="来源筛选"
            >
              <option value="">全部来源</option>
              {sources.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABELS[s] || s}
                </option>
              ))}
            </select>
          )}
          {presetPacks.length > 0 && (
            <select
              value={presetPack}
              onChange={(e) => setPresetPack(e.target.value)}
              style={{ flex: 1, minWidth: 140 }}
              aria-label="预置包筛选"
            >
              <option value="">全部预置包</option>
              {presetPacks.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          )}
          {hasFilters && (
            <button className="secondary" onClick={clearFilters} style={{ padding: '8px 12px' }}>
              <X size={14} /> 清空
            </button>
          )}
        </div>
      </div>

      {filteredAssets.length === 0 ? (
        <div className="empty" style={{ padding: '48px 20px' }}>
          <SlidersHorizontal size={40} style={{ opacity: 0.4, marginBottom: 12 }} />
          <h3>未找到匹配资产</h3>
          <p className="text-muted">尝试调整搜索词或筛选条件</p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
            gap: 16,
          }}
        >
          {filteredAssets.map((asset) => (
            <AssetCard
              key={`${asset.type}-${asset.id}`}
              asset={asset}
              onPreview={setPreviewAsset}
              onToggle={onToggle}
              onApply={onApply}
              onEdit={onEdit}
              onSetGlobal={onSetGlobal}
              applyLabel={applyLabel}
              removeLabel={removeLabel}
            />
          ))}
        </div>
      )}

      <AssetPreviewDrawer
        asset={previewAsset}
        onClose={() => setPreviewAsset(null)}
        onToggle={onToggle}
        onApply={onApply}
        applyLabel={applyLabel}
        removeLabel={removeLabel}
      />
    </div>
  );
}
